import React from "react";
import { GridCellParams, GridColDef, gridFilterModelSelector, GridLogicOperator, GridRenderCellParams, GridRowModelUpdate, useGridApiContext } from "@mui/x-data-grid-pro";
import { Projection } from "../EasyProjection/types";
import { Box, Popper, Stack, TextField, Tooltip } from "@mui/material";
import { numberWithCommas } from "../../utils/stringUtils";
import { AddCircleOutline, Block, RemoveCircleOutline } from "@material-ui/icons";
import { Lock, LockOpen, LockOpenOutlined, LockOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { addPlayer, removePlayer, togglePlayerLock } from "../../LineupBuilder/store/actions";
import { SlateResponse } from "../store/types";
import { Replay } from "@mui/icons-material";
import ConfirmedLineupIcons from "./ConfirmedLineupIcons";
import StartingStatusIcons from "./StartingStatusIcons";
import { IRootState } from "../../../store/reducer";
import { Lineup } from "../../LineupBuilder/store/types";
import { canAddPlayerToSlot } from "../../LineupBuilder/helpers/specialPositionsHelper";

export const showdownPositions = ['CPT', 'MVP', 'MVP - 1.5x Points', 'FLEX', 'STAR', 'PRO', 'UTIL', 'AnyFLEX', 'MVP - 1.5X Points', 'S-FLEX', 'SFLEX'];
export const multipiers = { 'MVP': 2, 'MVP - 1.5X Points': 1.5, 'MVP - 1.5x Points': 1.5, 'STAR': 1.2, 'PRO': 1.5, 'CPT': 1.5, 'SFLEX': 1.5 }

export const ratingOnlyOperators = [
  {
    label: "Has",
    value: "has",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }
      return (value) => {
        return value.value?.includes(filterItem.value) ?? false;
      };
    },
  },
];

export  const showdownMultipiers = (player: Projection, position: string) => {
  if (showdownPositions.indexOf(position) > -1) {
    return {
      projection: multipiers[position] !== undefined ? multipiers[position] * player.projection : player.projection,
      fpp: multipiers[position] !== undefined ? multipiers[position] * player.projection : player.projection,
      ...(['CPT', 'MVP - 1.5X Points', 'MVP - 1.5x Points'].includes(position) && !player.canBeAddedToPositions.includes('FIGHTER') ? { salary: player.salary * 1.5 } : {})
    };
  }
}

const AddComponent = ({ params }: { params: GridRenderCellParams<Projection> }) => {
  const [inLineup, setOnLineup] = React.useState(false);
  const [cannotAdd, setCannotAdd] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { lineups, selectedLineupId } = useSelector((state: IRootState) => state.lineupBuilder);
  const { selectedSite } = useSelector((state: IRootState) => state.common);
  const dispatch = useDispatch();
  const apiRef = useGridApiContext();
  const filters = gridFilterModelSelector(apiRef);
  const filteredPosition = filters.items.find(x => x.field === 'canBeAddedToPositions');
  const filteredName = filters.items.find(x => x.field === 'name' || x.field === 'player');
  const currentFilterModel = gridFilterModelSelector(apiRef);

  const handleClick = (event: React.MouseEvent<HTMLElement>, singlePosition?: string) => {
    const multiPosition = params.row.position.includes('/');
    let bothPositionsAvailable = false;
    const lineup = lineups.find(x => x.id === selectedLineupId) || {} as Lineup;
    if (multiPosition) {
      bothPositionsAvailable = params.row.position.split('/').map(pos => {
        return lineup.players.find(y => y.position === pos && y.name === '') ? pos : null;
      }).filter(x => x !== null).length === params.row.position.split('/').length;
    }

    if (inLineup) {
      dispatch(removePlayer(params.row, selectedLineupId))
    } else if (multiPosition && bothPositionsAvailable && !singlePosition && !filteredPosition?.value) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
      return;
    } else if (singlePosition && !filteredPosition?.value) {
      dispatch(addPlayer({ ...params.row, position: singlePosition }, selectedLineupId, selectedSite.site))
      setAnchorEl(null);
    } else if (filteredPosition?.value) {
      const positionAvailableCount = lineup.players.filter(y => y.position === filteredPosition?.value && y.name === '').length;
      const playerToAdd = {
        ...params.row,
        position: filteredPosition?.value,
        // ...showdownMultipiers(params.row, filteredPosition?.value)
      }
      dispatch(addPlayer(playerToAdd, selectedLineupId, selectedSite.site))
      if (positionAvailableCount === 1) {
        const lineupPositions: string[] = [];
        lineup.players.forEach((player) => { if (player.name === '' && player.position !== filteredPosition?.value) { lineupPositions.push(player.position) } });
        if (lineupPositions.length === 0) {
          apiRef.current.setFilterModel({
            logicOperator: GridLogicOperator.And,
            items: [
              ...currentFilterModel.items.filter((item) => item.field !== 'canBeAddedToPositions'),
            ],
          })
        } else {
          apiRef.current.setFilterModel({
            logicOperator: GridLogicOperator.And,
            items: [
              ...currentFilterModel.items.filter((item) => item.field !== 'canBeAddedToPositions'),
              ...[{ field: 'canBeAddedToPositions', operator: 'has', value: lineupPositions[0] }]
            ],
          })
        }
      }
    } else {
      dispatch(addPlayer(params.row, selectedLineupId, selectedSite.site))
    }
    if(filteredName){
      apiRef.current.setFilterModel({
        items: [
          ...currentFilterModel.items.filter((item) => item.field !== 'name').filter((item) => item.field !== 'player'),
        ]
      })
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  React.useEffect(() => {
    if (lineups) {
      const lineup = lineups.find(x => x.id === selectedLineupId);
      let found, hasPositionAvailable = false;
      if (lineup) {
        found = lineup.players.find(y => y.name === params.row.name);
        hasPositionAvailable = lineup.players.filter(y =>
          canAddPlayerToSlot(params.row, y.position) && (filteredPosition
            ? y.position === filteredPosition.value
            : true
          )
          && y.name === '')
          ?.length > 0;
      }

      if (found) {
        setOnLineup(true);
      } else if (!hasPositionAvailable) {
        setCannotAdd(true);
      } else {
        setOnLineup(false);
        setCannotAdd(false);
      }
    }
  }, [lineups, selectedLineupId, filteredPosition]);

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" onClick={handleClick} aria-describedby={id}>
        <Box fontSize='12px' fontWeight={700} sx={{ cursor: 'pointer' }}>
          {
            inLineup ? <RemoveCircleOutline style={{ color: 'rgba(255, 67, 88, 0.50)' }} /> : cannotAdd ? <Block style={{ color: 'rgba(255, 255, 255, 0.50)' }} /> : <AddCircleOutline style={{ color: '#368D39' }} />
          }
        </Box>
      </Box>
      <Popper id={id} open={open} anchorEl={anchorEl} placement='left' sx={{
        padding: '8px',
        backgroundColor: 'rgba(35, 35, 35, 1)',
        borderRadius: '6px',
        fontFamily: 'Inter',
      }}>
        {
          params.row.position?.split('/').map((pos, index) => (
            <Stack onClick={(e) => handleClick(e, pos)} direction='row' alignItems='center' justifyContent='center' columnGap='4px' key={index} color='white' sx={{ cursor: 'pointer', padding: '4px', borderRadius: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' } }}>
              Add to {pos} <AddCircleOutline style={{ color: '#368D39' }} />
            </Stack>
          )
          )
        }
      </Popper>
    </>
  )
}

const MinusIcon = (props: any): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
      <rect width="16" height="16" rx="8" fill="#DF0019" fillOpacity="0.15" />
      <path d="M3.33337 8.5V7.5H12.6667V8.5H3.33337Z" fill="#DF0019" />
    </svg>
  )
}
const PlusIcon = (props: any): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
      <rect width="16" height="16" rx="8" fill="#00D008" fillOpacity="0.15" />
      <path d="M7.50004 12.6666V8.49992H3.33337V7.49992H7.50004V3.33325H8.50004V7.49992H12.6667V8.49992H8.50004V12.6666H7.50004Z" fill="#00D008" />
    </svg>
  )
}

const Cell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = (value: number) => {
    params.api.updateRows([{ ...params.row, projection: value < 0 ? 0 : value, fppm: value / params.row.minutes }])
    setTextFieldValue(value < 0 ? 0 : value?.toFixed(2));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.projection
    };

    const isGreater = parseFloat(params.value?.toFixed(2)) > parseFloat((original?.projection?.toFixed(2) || '0'));
    const isLess = parseFloat(params.value?.toFixed(2)) < parseFloat((original?.projection?.toFixed(2) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params?.value?.toFixed(2))
  }, [projections, params.value])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <Replay fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + 1)} ><PlusIcon /></Stack>
    </Stack>
  )
}

const OwnershipCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void, getOwnership: (proj: Projection | undefined, filteredPosition) => number | undefined, getRowUpdate: (projection: Projection, value: number) => GridRowModelUpdate, filteredPosition: string) => {
  const value = params.value ? params.value : 0;
  const [textFieldValue, setTextFieldValue] = React.useState(((value * 100)?.toFixed(2)));
  const handleClick = (value: number) => {
    params.api.updateRows([getRowUpdate(params.row, value)])
    setTextFieldValue(value < 0 ? '0' : value?.toFixed(2))
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }

  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);
    const own = getOwnership(original, filteredPosition);
    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: own?.toFixed(2),
      floatValue: own
    };

    const isGreater = parseFloat(value?.toFixed(2)) > parseFloat((own?.toFixed(2) || '0'));
    const isLess = parseFloat(value?.toFixed(2)) < parseFloat((own?.toFixed(2) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }


  React.useEffect(() => {
    setTextFieldValue((value * 100)?.toFixed(2))
  }, [filteredPosition, projections])

  const OGChanges = getOGProjChange();

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" style={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick((value ?? 0) * 100 - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue + '%'}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          size='small'
          InputProps={{
            endAdornment: OGChanges.value !== params.value?.toFixed(2) && <Replay fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick((OGChanges.floatValue || 0) * 100)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(value * 100 + 1)}><PlusIcon /></Stack>
    </Stack>
  )
}

export const playerTableHeaderConfig = (originalProjections: Projection[], projections: Projection[], updateSessionStorage: (newProjections: Projection[]) => void, slate: SlateResponse, disableCustomization: boolean, lineups: Lineup[], selectedLineupId: number, filteredPosition: string): GridColDef[] => {

  const name: GridColDef = {
    field: 'name',
    headerName: 'Player',
    flex: .2,
    align: 'left',
    headerAlign: 'left',
    renderCell(params: GridRenderCellParams<Projection>) {
      const { name, confirmedLineup, lineupPosition, starting, injuryStatus } = params.row;
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box fontSize='12px' fontWeight={700} style={{ textTransform: 'uppercase' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.70)' }}>{name?.split(' ')[0]}</span> {name?.split(' ')?.splice(1).join(' ')}
          </Box>
          {
            slate?.sport?.toLowerCase() === 'mlb' && (
              <Box paddingLeft='4px'>
                <ConfirmedLineupIcons confirmed={confirmedLineup} battingOrder={lineupPosition} />
              </Box>
            )
          }
          {
            (starting !== undefined || (injuryStatus && injuryStatus !== 'None' && injuryStatus !== 'Unknown')) && slate?.sport?.toLowerCase() !== 'mlb' && (
              <Box paddingLeft='4px'>
                <StartingStatusIcons starting={starting} confirmedLineup={confirmedLineup} injuryStatus={injuryStatus} />
              </Box>
            )
          }
        </Box>
      )
    },
  };
  const salary: GridColDef = {
    field: 'salary',
    headerName: 'Salary',
    flex: .1,
    align: 'left',
    type: 'number',
    valueGetter(params) {
      if (showdownPositions.indexOf(filteredPosition) > -1) {
        if (filteredPosition === 'CPT' || (filteredPosition === 'MVP - 1.5x Points' && (slate.sport === 'CFB' || slate.sport === 'NFL') && slate.site === 'FD' && slate.type === 'SHOWDOWN')) {
          return params.value * 1.5;
        }
        return params.value;
      }
      return params.value;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      return '$' + numberWithCommas(params.value);
    },
  };
  const position: GridColDef = {
    field: 'position',
    headerName: 'Pos.',
    width: 70,
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      // if (showdownPositions.indexOf(filteredPosition) > -1) {
      //   return filteredPosition;
      // }
      return params.value;
    },
  };
  const team: GridColDef = {
    field: 'team',
    headerName: 'Team',
    width: 80,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {
            ['mlb', 'nba', 'nhl', 'nfl'].indexOf(slate?.sport?.toLowerCase()) > -1 && (
              <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
                <img src={row.teamLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
              </Box>
            )
          }
          {params.value}
        </Stack>
      )
    }
  };
  const opponent: GridColDef = {
    field: 'opponent',
    headerName: 'Opp',
    width: 80,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {
            ['mlb', 'nba', 'nhl', 'nfl'].indexOf(slate?.sport?.toLowerCase()) > -1 && (
              <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
                <img src={row.opponentLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
              </Box>
            )
          }
          {params.value}
        </Stack>
      )
    }
  };
  const value: GridColDef = {
    field: 'test',
    headerName: 'Value',
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueGetter(params) {
      return ((params.row.projection / params.row.salary) * 1000).toFixed(2);
    },
  };

  const projection: GridColDef = {
    field: 'projection',
    headerName: 'Projection',
    flex: .2,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueGetter(params) {
      if (showdownPositions.indexOf(filteredPosition) > -1) {
        if(filteredPosition) {
          return multipiers[filteredPosition] !== undefined ? multipiers[filteredPosition] * params.value : params.value
        }
        return multipiers[params.row.position] !== undefined ? multipiers[params.row.position] * params.value : params.value
      }
      return params.value;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.projection?.toFixed(2) : Cell(params, originalProjections, projections, updateSessionStorage),
  };

  const ownership: GridColDef = {
    field: 'ownership',
    headerName: 'OWN',
    flex: .2,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueGetter(params) {
      return getOwnership(params.row, filteredPosition);
    },
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? (params.value ? (params.value * 100)?.toFixed(2) + '%' : '0%') : OwnershipCell(params, originalProjections, projections, updateSessionStorage, getOwnership, updateOwnership, filteredPosition),
  };
  const getOwnership = (projection: Projection | undefined, filteredPosition) => {
    const { proOwnership, starOwnership , cptOwnership, ownership } = projection || {};

    if (slate.type === 'SHOWDOWN') {
      if (!filteredPosition) { // ALL
        return (proOwnership || 0 ) + (starOwnership || 0 ) + (cptOwnership || 0 ) + (ownership || 0 );
      } else if (filteredPosition === 'MVP - 2X Points' || filteredPosition === 'CPT') {
        return cptOwnership;
      } else if (filteredPosition === 'STAR - 1.5X Points') {
        return starOwnership;
      } else if (filteredPosition === 'PRO - 1.2X Points') {
        return proOwnership;
      } else {
        return ownership;
      }
    } else {
       return ownership;
    }
  }
  const updateOwnership = (projection: Projection, value: number) => {
    return { ...projection, ownership: value < 0 ? 0 : value / 100 } as GridRowModelUpdate;
  }

  const addPlayer: GridColDef = {
    field: '',
    headerName: '',
    flex: .01,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      return (
        <AddComponent params={params} />
      )
    }
  };
  const canBeAddedToPositions: GridColDef = {
    field: 'canBeAddedToPositions',
    headerName: '',
    width: 0,
    headerAlign: 'center',
    align: 'center',
    filterOperators: [...ratingOnlyOperators],
  };


  let headers: GridColDef[] = [
    position,
    name,
    team,
    opponent,
    projection,
    ownership,
    salary,
    value,
    addPlayer,
    canBeAddedToPositions
  ];

  
  headers = headers.map((header) => ({
    ...header,
    cellClassName: (params: GridCellParams<Projection>) => {
      const GetLineups = () => {
        return lineups.find(x => x.id === selectedLineupId) || {} as Lineup;
      }
      const found = GetLineups()?.players?.find(y => y.nameAndId === params.row.nameAndId);
      
      if (found) {
        return 'removed';
      }
      return '';
    },
  }))
  
  if (['mlb', 'nba', 'nhl', 'nfl', 'cfb'].indexOf(slate?.sport?.toLowerCase()) < 0) {
    headers = headers.filter(x => x.field !== 'team');
    if (slate?.sport?.toLowerCase() !== 'mma') {
      headers = headers.filter(x => x.field !== 'opponent');
    }
  }
  
  return headers;
}

const LineupTableActions = ({ params }: { params: GridRenderCellParams<any> }) => {
  const dispatch = useDispatch();
  const { lineups, selectedLineupId } = useSelector((state: IRootState) => state.lineupBuilder);
  
  const handleRemove = () => {
    dispatch(removePlayer(params.row, selectedLineupId));
  };
  
  const handleToggleLock = () => {
    dispatch(togglePlayerLock(params.row.nameAndId, selectedLineupId));
  };
  
  // Don't show actions for empty slots
  if (!params.row.name) {
    return null;
  }
  
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title="Locked players remain in your lineup when you clear it.">
        <Box onClick={handleToggleLock} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {params.row.isLocked ? (
            <LockOutlined style={{ color: '#005CCE', fontSize: '20px' }} />
          ) : (
            <LockOpenOutlined style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '20px' }} />
          )}
        </Box>
      </Tooltip>
      <Box onClick={handleRemove} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <RemoveCircleOutline style={{ color: 'rgba(255, 67, 88, 0.50)', fontSize: '20px' }} />
      </Box>
    </Stack>
  );
};

export const lineupTableHeaderConfig = (slate: SlateResponse): GridColDef[] => {

  const name: GridColDef = {
    field: 'name',
    headerName: 'Player',
    flex: .2,
    align: 'left',
    headerAlign: 'left',
    renderCell(params: GridRenderCellParams<Projection>) {
      const { name, confirmedLineup, lineupPosition, starting, injuryStatus } = params.row;
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box fontSize='12px' fontWeight={700} style={{ textTransform: 'uppercase' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.70)' }}>{name?.split(' ')[0]}</span> {name?.split(' ')?.splice(1).join(' ')}
          </Box>
          {
            name && slate?.sport?.toLowerCase() === 'mlb' && (
              <Box paddingLeft='4px'>
                <ConfirmedLineupIcons confirmed={confirmedLineup} battingOrder={lineupPosition} />
              </Box>
            )
          }
          {
            (starting !== undefined || (injuryStatus && injuryStatus !== 'None' && injuryStatus !== 'Unknown')) && slate?.sport?.toLowerCase() !== 'mlb' && (
              <Box paddingLeft='4px'>
                <StartingStatusIcons starting={starting} confirmedLineup={confirmedLineup} injuryStatus={injuryStatus} />
              </Box>
            )
          }
        </Box>
      )
    },
  };
  const salary: GridColDef = {
    field: 'salary',
    headerName: 'Salary',
    flex: .1,
    align: 'left',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (!params.value) {
        return ''
      }
      return '$' + numberWithCommas(params.value);
    },
  };
  const position: GridColDef = {
    field: 'position',
    headerName: 'Pos.',
    width: 70,
    headerAlign: 'center',
    align: 'center',
  };
  const team: GridColDef = {
    field: 'team',
    headerName: 'Team',
    width: 80,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {
            row.teamLogo && (
              <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
                <img src={row.teamLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
              </Box>
            )
          }
          {params.value}
        </Stack>
      )
    }
  };
  const opponent: GridColDef = {
    field: 'opponent',
    headerName: 'Opp',
    width: 80,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {
            row.opponentLogo && (
              <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
                <img src={row.opponentLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
              </Box>
            )
          }
          {params.value}
        </Stack>
      )
    }
  };
  const projection: GridColDef = {
    field: 'fpp',
    headerName: 'FPP',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => params.row.fpp ? params.row.fpp?.toFixed(2) : '',
  };

  const ownership: GridColDef = {
    field: 'ownership',
    headerName: 'OWN',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueGetter(params) {
      const position = params?.row?.position;
      if (['MVP - 2X Points', 'MVP - 1.5X Points', 'MVP - 1.5x Points', 'CPT'].includes(position)) {
        return params.row.cptOwnership;
      } else if (position === 'STAR - 1.5X Points') {
        return params.row.starOwnership;
      } else if (position === 'PRO - 1.2X Points') {
        return params.row.proOwnership;
      } else {
        return params.row.ownership;
      }
    },
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
    renderCell: (params: GridRenderCellParams<Projection>) => (params.value ? (params.value * 100)?.toFixed(2) + '%' : ''),
  };

  const totalOwnership: GridColDef = {
    field: 'totalOwnership',
    headerName: 'TOT OWN',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueGetter(params) {
      const { proOwnership, starOwnership ,  cptOwnership, ownership } = params.row;
      const totalOwnership = (proOwnership || 0 ) + (starOwnership || 0 ) + (cptOwnership || 0 ) + (ownership || 0 );
      return totalOwnership;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => (params.value ? (params.value * 100)?.toFixed(2) + '%' : ''),
  };


  const addPlayer: GridColDef = {
    field: '',
    headerName: '',
    flex: .12,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      return (
        <LineupTableActions params={params} />
      )
    }
  };


  let headers: GridColDef[] = [
    position,
    name,
    team,
    opponent,
    projection,
    ownership,
    totalOwnership,
    salary,
    addPlayer,
  ];

  if (['mlb', 'nba', 'nhl', 'nfl', 'cfb'].indexOf(slate?.sport?.toLowerCase()) < 0) {
    headers = headers.filter(x => x.field !== 'team');
    if (slate.sport.toLowerCase() !== 'mma') {
      headers = headers.filter(x => x.field !== 'opponent');
    }
  }

  if (slate?.type === 'CLASSIC') {
    headers = headers.filter(x => x.field !== 'totalOwnership');
  }

  return headers;
}
